/**
 * BoardPage - Collaborative whiteboard page with Yjs sync
 *
 * This page integrates:
 * - Yjs for real-time collaboration (Y.Doc, WebSocket provider)
 * - React Flow via BoardCanvas component
 * - Awareness for cursor/presence sync
 * - Undo/redo with per-user tracking
 *
 * The canvas logic is extracted to BoardCanvas for separation of concerns.
 */

import '@xyflow/react/dist/style.css';
import { useEffect, useCallback, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ReactFlowProvider, type Node } from '@xyflow/react';
import {
  useYDoc,
  useYjsProvider,
  useAwareness,
  useUndoManager,
  useUndoShortcuts,
} from '../collaboration';
import { AvatarStack } from '../components/presence/AvatarStack';
import { ConnectionStatus } from '../components/sync/ConnectionStatus';
import { BoardSkeleton } from '../components/sync/BoardSkeleton';
import { UndoRedoToolbar } from '../components/sync/UndoRedoToolbar';
import { BoardCanvas } from '../components/whiteboard/BoardCanvas';
import { PropertyPanel, TemplateGallery } from '../components/panels';
import { RichTextEditor } from '../components/editor';
import { ChatSidebar } from '../components/chat';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { usePermissions } from '../hooks/usePermissions';
import { ThemeProvider } from '../components/themes';

// ============================================================================
// MAIN BOARD PAGE
// ============================================================================

export function BoardPage() {
  const { boardId } = useParams<{ boardId: string }>();
  const navigate = useNavigate();

  // Get current user for awareness
  const { user: currentUser, isLoading: userLoading, error: userError } = useCurrentUser();

  // Redirect to signin if not authenticated
  useEffect(() => {
    if (userError) {
      navigate('/signin');
    }
  }, [userError, navigate]);

  // Initialize Yjs document with IndexedDB persistence
  const { ydoc, localSynced } = useYDoc(boardId || '');

  // Connect to server via WebSocket
  // wsRef is exposed for AI Ghost Writer requests (Phase 7)
  const { status, awareness, reconnect, wsRef } = useYjsProvider(ydoc, boardId || '');

  // Set up awareness with current user
  const { states, setCursor, onlineUsers } = useAwareness(
    awareness,
    currentUser || { id: 'anonymous', name: 'Anonymous', color: '#888888' }
  );

  // Set up per-user undo/redo
  // CRITICAL: makeChange MUST be used to wrap all Y.Doc modifications for undo to work
  const { canUndo, canRedo, undo, redo, makeChange } = useUndoManager(
    ydoc,
    currentUser?.id || 'anonymous'
  );

  // Register keyboard shortcuts for undo/redo
  useUndoShortcuts(undo, redo);

  // ---------------------------------------------------------------------------
  // PROPERTY PANEL STATE
  // Track selected node and panel visibility for property editing
  // ---------------------------------------------------------------------------
  const [selectedNode, setSelectedNode] = useState<Node | null>(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  // ---------------------------------------------------------------------------
  // TEMPLATE GALLERY STATE
  // Track whether the template gallery modal is open
  // ---------------------------------------------------------------------------
  const [isTemplatesOpen, setIsTemplatesOpen] = useState(false);

  // ---------------------------------------------------------------------------
  // VIEW MODE STATE
  // Toggle between canvas (whiteboard) and document (rich text) views
  // ---------------------------------------------------------------------------
  const [viewMode, setViewMode] = useState<'canvas' | 'document'>('canvas');

  // ---------------------------------------------------------------------------
  // CHAT SIDEBAR STATE
  // Collapsible chat sidebar for AI conversations (HOME-03, HOME-07)
  // ---------------------------------------------------------------------------
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isKnowledgeLoading, setIsKnowledgeLoading] = useState(false);

  // Get RBAC permissions for the board (HOME-12)
  const permissions = usePermissions();

  /**
   * Handle node selection changes from BoardCanvas.
   * Opens property panel when exactly one node is selected.
   */
  const handleNodeSelectionChange = useCallback((nodes: Node[]) => {
    if (nodes.length === 1) {
      // Single node selected - show in property panel
      setSelectedNode(nodes[0]);
      setIsPanelOpen(true);
    } else {
      // No selection or multi-select - clear selection (keep panel open if user prefers)
      setSelectedNode(null);
    }
  }, []);

  /**
   * Handle property updates from PropertyPanel.
   *
   * CRITICAL: Uses makeChange wrapper for Yjs undo/redo tracking.
   * Updates the node data in the Yjs map, which syncs to all collaborators.
   */
  const handlePropertyUpdate = useCallback(
    (nodeId: string, data: Record<string, unknown>) => {
      // Get the nodesMap from Yjs document
      const nodesMap = ydoc.getMap<Node>('nodes');
      const existingNode = nodesMap.get(nodeId);

      if (!existingNode) return;

      // CRITICAL: Wrap Y.Map updates in makeChange() for undo tracking
      makeChange(() => {
        nodesMap.set(nodeId, {
          ...existingNode,
          data: { ...existingNode.data, ...data },
        });
      });

      // Update local selectedNode state for immediate UI feedback
      // (Yjs observer will also update, but this gives instant response)
      setSelectedNode((prev) =>
        prev && prev.id === nodeId
          ? { ...prev, data: { ...prev.data, ...data } }
          : prev
      );
    },
    [ydoc, makeChange]
  );

  /**
   * Close the property panel.
   */
  const handlePanelClose = useCallback(() => {
    setIsPanelOpen(false);
  }, []);

  /**
   * Open the template gallery modal.
   * Called from NodeToolbar via BoardCanvas.
   */
  const handleOpenTemplates = useCallback(() => {
    setIsTemplatesOpen(true);
  }, []);

  /**
   * Close the template gallery modal.
   */
  const handleCloseTemplates = useCallback(() => {
    setIsTemplatesOpen(false);
  }, []);

  // Handle avatar click
  const handleUserClick = useCallback((user: { id: string; name: string }) => {
    console.log(`[BoardPage] Jump to user: ${user.name}`);
  }, []);

  const handleOpenKnowledge = useCallback(async () => {
    if (!boardId) return;
    setIsKnowledgeLoading(true);

    try {
      const response = await fetch(`/api/kb/boards/${boardId}/space`, {
        credentials: 'include',
      });
      const data = await response.json();
      if (response.ok && data?.data?.space?.id) {
        navigate(`/knowledge/${data.data.space.id}`);
      }
    } catch (error) {
      console.error('Failed to open knowledge space:', error);
    } finally {
      setIsKnowledgeLoading(false);
    }
  }, [boardId, navigate]);

  // Show loading state
  if (userLoading || !localSynced) {
    return <BoardSkeleton message={userLoading ? 'Authenticating...' : 'Syncing board...'} />;
  }

  // Show error if no boardId
  if (!boardId) {
    return (
      <div className="flex h-screen items-center justify-center bg-color-main">
        <p className="text-color-danger">Board ID is required</p>
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col bg-color-main text-white font-inter">
      {/* Header */}
      <header className="relative flex h-16 items-center justify-between border-b border-color-main-darker bg-color-main-darker/90 px-4 backdrop-blur-md shadow-card">
        {/* Left: Board title */}
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/')}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-color-blended hover:bg-color-main hover:text-white transition-colors"
            title="Back to Dashboard"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>

          <div className="flex flex-col">
            <h1 className="text-[15px] font-medium text-white font-manrope leading-tight">
              Board: {boardId}
            </h1>
            <div className="flex items-center gap-2">
              {/* Connection status badge */}
              <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-medium border ${status === 'connected' ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' :
                  status === 'connecting' ? 'bg-amber-500/10 border-amber-500/20 text-amber-400' :
                    'bg-red-500/10 border-red-500/20 text-red-400'
                }`}>
                <div className={`h-1.5 w-1.5 rounded-full ${status === 'connected' ? 'bg-emerald-400' :
                    status === 'connecting' ? 'animate-pulse bg-amber-400' :
                      'bg-red-400'
                  }`} />
                {status === 'connected' ? 'Connected' : status === 'connecting' ? 'Connecting...' : 'Disconnected'}
              </div>
            </div>
          </div>

          <div className="h-6 w-[1px] bg-color-main-darker mx-2" />

          {/* View mode toggle - switch between canvas and document */}
          <div className="flex items-center rounded-lg bg-color-main p-1 border border-color-main-darker shadow-inner">
            <button
              type="button"
              onClick={() => setViewMode('canvas')}
              className={`flex h-7 w-8 items-center justify-center rounded-md transition-all ${viewMode === 'canvas'
                  ? 'bg-color-main-darker text-color-vivo shadow-sm'
                  : 'text-color-blended hover:text-white hover:bg-white/5'
                }`}
              title="Canvas view"
            >
              {/* Grid/whiteboard icon */}
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <rect x="7" y="7" width="4" height="4" rx="1" />
                <rect x="13" y="7" width="4" height="4" rx="1" />
                <rect x="7" y="13" width="4" height="4" rx="1" />
              </svg>
            </button>
            <button
              type="button"
              onClick={() => setViewMode('document')}
              className={`flex h-7 w-8 items-center justify-center rounded-md transition-all ${viewMode === 'document'
                  ? 'bg-color-main-darker text-color-vivo shadow-sm'
                  : 'text-color-blended hover:text-white hover:bg-white/5'
                }`}
              title="Document view"
            >
              {/* Document icon */}
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
              </svg>
            </button>
          </div>
        </div>

        {/* Center: Undo/Redo toolbar (Absolute) */}
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          <div className="rounded-xl bg-color-main border border-color-main-darker shadow-card p-1">
            <UndoRedoToolbar
              canUndo={canUndo}
              canRedo={canRedo}
              onUndo={undo}
              onRedo={redo}
            />
          </div>
        </div>

        {/* Right: Presence indicators */}
        <div className="flex items-center gap-4">
          {onlineUsers.length > 0 && (
            <span className="text-xs text-color-blended font-medium">
              {onlineUsers.length} other{onlineUsers.length > 1 ? 's' : ''} online
            </span>
          )}

          <div className="flex items-center -space-x-2">
            <AvatarStack
              users={onlineUsers}
              maxVisible={3}
              onUserClick={handleUserClick}
            />

            {currentUser && (
              <div
                className="relative flex h-8 w-8 items-center justify-center rounded-full border-2 border-color-main shadow-sm"
                style={{ backgroundColor: currentUser.color }}
                title={`${currentUser.name} (you)`}
              >
                <span className="text-xs font-bold text-white">
                  {currentUser.name.charAt(0).toUpperCase()}
                </span>
                <div className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full border-2 border-color-main bg-emerald-500" />
              </div>
            )}
          </div>

          <button
            onClick={handleOpenKnowledge}
            className="flex h-9 items-center gap-2 rounded-lg border border-color-main-darker bg-color-main px-3 text-xs font-medium text-color-blended hover:text-white hover:border-color-blended transition-all"
            title="Open board knowledge space"
            disabled={isKnowledgeLoading}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 7h6v10H3zM15 7h6v10h-6zM9 5h6v14H9z" />
            </svg>
            {isKnowledgeLoading ? 'Opening...' : 'Knowledge'}
          </button>

          {permissions.canAccessChat && (
            <button
              onClick={() => setIsChatOpen(!isChatOpen)}
              className={`flex h-9 w-9 items-center justify-center rounded-lg border transition-all ${isChatOpen
                  ? 'bg-color-vivo text-color-purple-dark border-color-vivo'
                  : 'bg-color-main border-color-main-darker text-color-blended hover:text-white hover:border-color-blended'
                }`}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
              </svg>
            </button>
          )}
        </div>
      </header>

      {/* Main board area - switches between canvas and document view */}
      <main className="flex flex-1 overflow-hidden relative">
        {viewMode === 'canvas' ? (
          /* Canvas view - React Flow whiteboard */
          <>
            <div className="absolute inset-0 z-0">
              {/* Background grid pattern handled by ReactFlow, but container background is set */}
              {/* ThemeProvider enables switching between modern and classic Mac OS themes */}
              <ThemeProvider>
                <ReactFlowProvider>
                  <BoardCanvas
                    ydoc={ydoc}
                    awareness={awareness}
                    boardId={boardId}
                    currentUser={currentUser || { id: 'anonymous', name: 'Anonymous', color: '#888888' }}
                    states={states}
                    localClientId={ydoc.clientID}
                    setCursor={setCursor}
                    makeChange={makeChange}
                    onNodeSelectionChange={handleNodeSelectionChange}
                    onOpenTemplates={handleOpenTemplates}
                    wsRef={wsRef}
                  />
                </ReactFlowProvider>
              </ThemeProvider>
            </div>

            {/* Property Panel - only show in canvas mode when node selected */}
            {isPanelOpen && (
              <PropertyPanel
                selectedNode={selectedNode}
                onUpdate={handlePropertyUpdate}
                onClose={handlePanelClose}
              />
            )}
          </>
        ) : (
          /* Document view - Rich text editor with Tiptap */
          <div className="flex h-full w-full justify-center overflow-y-auto bg-color-neutral-1 p-8">
            <div className="w-full max-w-4xl rounded-xl bg-white p-12 shadow-card min-h-[calc(100vh-140px)] text-color-main">
              {awareness && (
                <RichTextEditor
                  ydoc={ydoc}
                  awareness={awareness}
                  currentUser={currentUser || { id: 'anonymous', name: 'Anonymous', color: '#888888' }}
                  boardId={boardId}
                  placeholder="Start typing or press / for commands..."
                  wsRef={wsRef}
                />
              )}
            </div>
          </div>
        )}
      </main>

      {/* Connection status modal */}
      <ConnectionStatus status={status} onReconnect={reconnect} />

      {/* Template gallery modal */}
      <TemplateGallery
        isOpen={isTemplatesOpen}
        onClose={handleCloseTemplates}
        nodesMap={ydoc.getMap<Node>('nodes')}
        makeChange={makeChange}
      />

      {/* Chat sidebar (HOME-03, HOME-07, HOME-12) */}
      {permissions.canAccessChat && (
        <ChatSidebar
          boardId={boardId}
          isOpen={isChatOpen}
          onToggle={() => setIsChatOpen(!isChatOpen)}
        />
      )}
    </div>
  );
}
